import { useCallback, useEffect, useState } from 'react';

import { useToaster } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { updateAliceAgentAppMcpTimeout } from 'client/api/services/aliceAgentApps';
import { useFetchingHandler } from 'client/hooks/useFetchingHandler';
import { getErrorMessage } from 'common/utils/errorsHandler';
import * as keyset from 'i18n/AliceExternalAppForm';

const t = i18n(keyset);

const MIN_TIMEOUT = 1;
const MAX_TIMEOUT = 1800;
const isInRange = (value: number) => Number.isInteger(value) && value >= MIN_TIMEOUT && value <= MAX_TIMEOUT;

interface Params {
    skillId: string;
    /** Current override from the DTO. Absent/`null` means the agent default is in effect. */
    mcpTimeoutSeconds?: number | null;
    onSaved: () => Promise<unknown> | void;
}

export const useMcpTimeout = ({ skillId, mcpTimeoutSeconds, onSaved }: Params) => {
    const { add: toasterAdd } = useToaster();
    const [value, setValue] = useState(mcpTimeoutSeconds?.toString() ?? '');

    useEffect(() => {
        setValue(mcpTimeoutSeconds?.toString() ?? '');
    }, [mcpTimeoutSeconds]);

    const parsed: number | null = value === '' ? null : Number(value);
    const valid = parsed === null || isInRange(parsed);
    const isDirty = parsed !== (mcpTimeoutSeconds ?? null);

    const [saveTimeout, saveState] = useFetchingHandler({
        handler: (timeoutSeconds: number | null) => updateAliceAgentAppMcpTimeout(skillId, timeoutSeconds),
        throwErrors: true,
    });
    const isSaving = saveState === 'loading';

    const handleSave = useCallback(async () => {
        if (!valid || !isDirty || isSaving) return;
        try {
            await saveTimeout(parsed);
            await onSaved();
            toasterAdd({ title: t('toast-mcp-timeout-saved'), theme: 'success', name: 'MCP_TIMEOUT' });
        } catch (err) {
            toasterAdd({
                title: getErrorMessage(err) ?? t('toast-mcp-timeout-error'),
                theme: 'danger',
                name: 'MCP_TIMEOUT',
            });
        }
    }, [valid, isDirty, isSaving, saveTimeout, parsed, onSaved, toasterAdd]);

    return { value, onUpdate: setValue, valid, isDirty, isSaving, handleSave };
};
